import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { dioQuotes } from './config/content';
import './App.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('DIO Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-message" style={{ margin: '50px auto', maxWidth: '600px' }}>
          <h2>🦇 ZA WARUDO! Time has stopped... 🦇</h2>
          <p>Something bizarre happened! DIO encountered an unexpected error.</p>
          <p style={{ fontStyle: 'italic' }}>"You thought the app would work perfectly, but it was me, ERROR!"</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '20px' }}
          >
            🔄 Restart the Adventure
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {  const [image, setImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState('');
  const [imageHistory, setImageHistory] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [imageError, setImageError] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [currentSound, setCurrentSound] = useState('');
  const [availableSounds, setAvailableSounds] = useState([]);
  const [currentImageData, setCurrentImageData] = useState(null);

  // Initialize app on component mount
  useEffect(() => {
    fetchRandomImage();
    setRandomQuote();
    fetchAvailableSounds();
  }, []);
    // Function to fetch available sounds from the server
  const fetchAvailableSounds = async () => {
    try {
      const response = await axios.get('/api/sounds');
      if (response.data && response.data.sounds) {
        setAvailableSounds(response.data.sounds);
      }
    } catch (error) {
      console.error('Error fetching sounds:', error);
    }
  };  const fetchRandomImage = async (format = null) => {
    setLoading(true);
    setError('');
    setImageError(false);
    try {
      let url = '/api/random-image';
      if (format) {
        url += `?format=${format}`;
      }
        const response = await axios.get(url);
      const imageData = response.data.image;
      const newImage = imageData.path;
      
      setImage(newImage);
      setCurrentImageData(imageData);
      setImageLoading(true); // Start loading the new image
      
      // Add to history (keep last 5 images)
      setImageHistory(prev => {
        const newHistory = [{url: newImage, data: imageData}, ...prev.filter(img => img.url !== newImage)];
        return newHistory.slice(0, 5);
      });
    } catch (error) {
      console.error('Error fetching image:', error);
      setError('Failed to fetch image. Please try again.');      // Set a guaranteed working fallback
      try {
        const response = await axios.get('/api/random-image');
        const imageData = response.data.image;
        const newImage = imageData.path;
        setImage(newImage);
        setCurrentImageData(imageData);
      } catch (fallbackError) {
        console.error('Even fallback image failed:', fallbackError);
        setError('Failed to load any images. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    // Try fetching a new image instead of showing a fallback
    fetchRandomImage();
  };
  const setRandomQuote = () => {
    const randomQuote = dioQuotes[Math.floor(Math.random() * dioQuotes.length)];
    setQuote(randomQuote);
  };  const loadPreviousImage = (imageData) => {
    setImage(imageData.url || imageData.path);
    setCurrentImageData(imageData.data || imageData);
    setImageLoading(true);
    setImageError(false);
  };const playDioSound = async () => {
    setAudioLoading(true);
    try {
      // Ensure we have sounds to choose from
      if (availableSounds.length === 0) {
        await fetchAvailableSounds();
        if (availableSounds.length === 0) {
          throw new Error('No sounds available');
        }
      }
      
      const randomSound = availableSounds[Math.floor(Math.random() * availableSounds.length)];
      setCurrentSound(randomSound.name);
      
      // Create audio element and preload the sound
      const audio = new Audio();
      
      // Set volume
      audio.volume = 0.7; // Set volume to 70%
      
      // Handle audio events
      const handleAudioEnd = () => {
        setCurrentSound('');
        setAudioLoading(false);
        audio.removeEventListener('ended', handleAudioEnd);
        audio.removeEventListener('error', handleAudioError);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      };
      
      const handleAudioError = (error) => {
        console.error('Error playing DIO sound:', error);
        console.error('Attempted path:', audio.src);
        setCurrentSound('🔊 Audio file not found!');
        setAudioLoading(false);
        setTimeout(() => {
          setCurrentSound('');
        }, 2000);
        audio.removeEventListener('ended', handleAudioEnd);
        audio.removeEventListener('error', handleAudioError);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      };
      
      // New handler for when audio is ready to play
      const handleCanPlayThrough = async () => {
        try {
          await audio.play();
        } catch (playError) {
          console.error('Audio play error:', playError);
          setCurrentSound('🔊 Click again to enable sound!');
          setAudioLoading(false);
          setTimeout(() => {
            setCurrentSound('');
          }, 2000);
        }
      };
      
      audio.addEventListener('ended', handleAudioEnd);
      audio.addEventListener('error', handleAudioError);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      
      // Set the source after adding event listeners
      audio.src = `${process.env.PUBLIC_URL}/assets/sounds/${randomSound.file}`;
      audio.load(); // Explicitly load the audio
      
    } catch (error) {
      console.error('Error playing DIO sound:', error);
      setCurrentSound('🔊 DIO Sound System Error!');
      setAudioLoading(false);
      setTimeout(() => {
        setCurrentSound('');
      }, 2000);
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>🦇 DIO BRANDO 🦇</h1>
        <h3>JOJO'S BIZARRE ADVENTURE</h3>
      </header>
      
      <div className="content">
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>        )}

        {quote && (
          <div className="quote-container">
            "{quote}"
            <div className="quote-author">- Dio Brando</div>
          </div>
        )}
          {currentImageData && currentImageData.isAnimated && (
          <div className="image-info">
            🎬 Animated GIF!
          </div>
        )}
          {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            Loading the power of DIO...
          </div>
        ) : (
          image && (
            <div style={{ position: 'relative' }}>
              {imageLoading && (
                <div className="loading" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                  <div className="loading-spinner"></div>
                  Loading image...
                </div>
              )}
              <img 
                src={image} 
                alt="Dio Brando" 
                className="dio-image"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ 
                  opacity: imageLoading ? 0.3 : 1,
                  transition: 'opacity 0.3s ease'
                }}              />
            </div>
          )
        )}        <div className="button-container">
          <button 
            onClick={() => fetchRandomImage()} 
            disabled={loading}
            className={loading ? "button-disabled" : ""}
          >
            🎲 Random Dio Image
          </button>
          <button onClick={setRandomQuote}>
            💬 New Quote
          </button>
          <button 
            onClick={playDioSound}
            disabled={audioLoading}
            className={audioLoading ? "button-disabled" : ""}
          >
            🔊 Play DIO Sound
          </button>
        </div>

        <div className="format-buttons">
          <button onClick={() => fetchRandomImage('gif')} disabled={loading} title="Animated GIFs">
            🎬 GIF
          </button>
          <button onClick={() => fetchRandomImage('png')} disabled={loading} title="PNG Images">
            🖼️ PNG
          </button>
          <button onClick={() => fetchRandomImage('jpg')} disabled={loading} title="JPG Images">
            📸 JPG
          </button>
        </div>

        {currentSound && (
          <div className="sound-indicator">
            🎵 Playing: "{currentSound}" 🎵
          </div>
        )}        {imageHistory.length > 1 && (
          <div className="image-history">
            <h4 style={{ color: '#e0b30c', marginBottom: '15px' }}>Recent Images:</h4>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {imageHistory.slice(1).map((imgData, index) => (
                <img
                  key={index}
                  src={imgData.url || imgData.path || imgData.data?.path}
                  alt={`Previous Dio ${index + 1}`}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    border: '2px solid #8B0000',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'                  }}
                  onClick={() => loadPreviousImage(imgData)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.borderColor = '#e0b30c';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.borderColor = '#8B0000';
                  }}
                />
              ))}
            </div>
          </div>        )}

      </div>
      
      <footer className="app-footer">
        <p>🔥 JOJO'S BIZARRE ADVENTURE Fan App - 2025 🔥</p>
        <p style={{ fontSize: '14px', marginTop: '5px', opacity: 0.8 }}>
          "You expected a normal footer, but it was me, Dio!"
        </p>
      </footer>
    </div>
  );
}

export default function Root() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
